import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { Box, Tabs, Tab, Tooltip } from '@mui/material';

import QuestionnaireForm from 'src/features/questionnaire/components/QuestionnaireForm';
import {
	a11yProps,
	CustomTabPanel,
} from 'src/features/questionnaire/pages/QuestionnairePage';
import { QUESTIONNAIRE_LOADING_KEY } from 'src/features/questionnaire/store';
import { IQuestionnaireTemplate } from 'src/features/questionnaire_template/domain/types';
import {
	getQuestionnaireTemplates,
	QUESTIONNAIRE_TEMPLATE_LOADING_KEY,
} from 'src/features/questionnaire_template/store/questionnaireTemplateThunks';
import { AppDispatch } from 'src/store';

const QuestionnairePanel: React.FC = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { siteSlug } = useParams<{ siteSlug: string }>();
	const [searchParams, setSearchParams] = useSearchParams();

	const isTemplateLoading = useSelector(
		(state: { loading: ILoadingState }) => {
			return state.loading.loadingStates[
				QUESTIONNAIRE_TEMPLATE_LOADING_KEY
			];
		},
	);
	const isQuestionnaireLoading = useSelector(
		(state: { loading: ILoadingState }) => {
			return state.loading.loadingStates[QUESTIONNAIRE_LOADING_KEY];
		},
	);

	const [activeQuestionnaireId, setActiveQuestionnaireId] = useState<
		string | null
	>();
	const [questionnaireTemplates, setQuestionnaireTemplates] = useState<
		IQuestionnaireTemplate[] | null
	>(null);

	const tabsRef = useRef<HTMLDivElement>(null);

	const handleChange = (
		event: React.SyntheticEvent | undefined,
		newValue: string,
	) => {
		setActiveQuestionnaireId(newValue);

		const newParams = new URLSearchParams(searchParams);
		newParams.set('questionnaire_id', newValue);
		setSearchParams(newParams);
	};

	useEffect(() => {
		if (!siteSlug) return;

		dispatch(
			getQuestionnaireTemplates({
				siteSlug: siteSlug,
				requestParams: {
					sort: 'index',
					order: 'asc',
					only_published: true,
				},
				onSuccess: (response) => {
					if (response.length > 0) {
						setQuestionnaireTemplates(response);

						const questionnaireID =
							searchParams.get('questionnaire_id');

						const isValidId = response.some(
							(template) =>
								String(template.id) === questionnaireID,
						);

						if (isValidId) {
							setActiveQuestionnaireId(questionnaireID);
						} else {
							handleChange(undefined, String(response[0].id));
						}
					}
				},
			}),
		);
	}, [siteSlug]);

	useEffect(() => {
		if (tabsRef.current) {
			const selectedTab = tabsRef.current.querySelector(
				`[role="tab"][aria-selected="true"]`,
			);
			if (selectedTab) {
				(selectedTab as HTMLElement).scrollIntoView({
					behavior: 'smooth',
					inline: 'start',
					block: 'nearest',
				});
			}
		}
	}, [activeQuestionnaireId]);

	return (
		<>
			{isTemplateLoading || !questionnaireTemplates ? (
				<Box sx={{ p: 3 }}>No questionnaire template available.</Box>
			) : (
				activeQuestionnaireId && (
					<>
						<Box
							sx={{
								position: 'sticky',
								top: 0,
								backgroundColor: 'white',
								zIndex: 100,
								borderBottom: 1,
								borderColor: 'divider',
							}}
						>
							<Tabs
								ref={tabsRef}
								value={activeQuestionnaireId}
								onChange={handleChange}
								aria-label='questionnaire tabs'
								variant='scrollable'
								scrollButtons='auto'
							>
								{questionnaireTemplates.map((template) => (
									<Tab
										key={template.id}
										value={String(template.id)}
										label={
											<Tooltip
												title={template.name}
												arrow
											>
												<span
													style={{
														display: 'inline-block',
														maxWidth: 180,
														overflow: 'hidden',
														textOverflow:
															'ellipsis',
														whiteSpace: 'nowrap',
														fontSize: '0.75rem',
													}}
												>
													{template.name}
												</span>
											</Tooltip>
										}
										disabled={
											isQuestionnaireLoading &&
											searchParams.get(
												'questionnaire_id',
											) !== String(template.id)
										}
										{...a11yProps(
											`${template.name}-${template.id}`,
										)}
									/>
								))}
							</Tabs>
						</Box>
						{questionnaireTemplates.map((templates) => (
							<CustomTabPanel
								key={templates.id}
								value={activeQuestionnaireId}
								index={String(templates.id)}
							>
								{activeQuestionnaireId && (
									<QuestionnaireForm
										questionnaireId={Number(
											activeQuestionnaireId,
										)}
										isFormDisabled={true}
										compactMode={true}
									/>
								)}
							</CustomTabPanel>
						))}
					</>
				)
			)}
		</>
	);
};

export default QuestionnairePanel;
